import React, { useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "react-toastify";
import PasswordInput from "../PasswordInput";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const SMTP_TLS_OPTIONS = [
    { value: "starttls", label: "STARTTLS (Recommended)" },
    { value: "ssl_tls", label: "SSL/TLS" },
    { value: "none", label: "None" },
];

const INITIAL_FORM = {
    account_identifier: "",
    smtp_host: "",
    smtp_port: "",
    smtp_password: "",
    smtp_tls_ssl: "starttls",
};

const validateSmtpForm = (form) => {
    const errors = {};
    const accountEmail = form.account_identifier.trim();
    const smtpHost = form.smtp_host.trim();
    const smtpPort = form.smtp_port.trim();

    if (!accountEmail) {
        errors.account_identifier = "Account email is required.";
    } else if (!EMAIL_PATTERN.test(accountEmail)) {
        errors.account_identifier = "Enter a valid account email.";
    }

    if (!smtpHost) {
        errors.smtp_host = "SMTP host is required.";
    }

    if (!smtpPort) {
        errors.smtp_port = "Port is required.";
    } else if (!Number.isFinite(Number(smtpPort))) {
        errors.smtp_port = "Port must be a number.";
    }

    if (!form.smtp_password) {
        errors.smtp_password = "Password is required.";
    }

    if (!form.smtp_tls_ssl) {
        errors.smtp_tls_ssl = "TLS/SSL option is required.";
    }

    return errors;
};

const getFieldClassName = (hasError, paddingClassName = "px-3") =>
    `mt-1 w-full rounded-xl border bg-white ${paddingClassName} py-2 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:ring-2 ${
        hasError
            ? "border-red-300 focus:border-red-400 focus:ring-red-100"
            : "border-slate-200 focus:border-indigo-400 focus:ring-indigo-100"
    }`;

const FieldError = ({ message }) =>
    message ? (
        <p className="mt-1 text-xs font-medium text-red-600">{message}</p>
    ) : null;

const SmtpConnectionForm = ({ onSubmit, isSubmitting = false }) => {
    const [form, setForm] = useState(INITIAL_FORM);
    const [errors, setErrors] = useState({});
    const [isTlsSelectOpen, setIsTlsSelectOpen] = useState(false);

    const handleFieldChange = (field, value) => {
        setForm((currentForm) => ({
            ...currentForm,
            [field]: value,
        }));
        setErrors((currentErrors) => {
            if (!currentErrors[field]) return currentErrors;

            const nextErrors = { ...currentErrors };
            delete nextErrors[field];
            return nextErrors;
        });
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        const validationErrors = validateSmtpForm(form);

        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            toast.error("Please complete the highlighted SMTP fields.");
            return;
        }

        setErrors({});
        const wasCreated = await onSubmit?.({
            account_identifier: form.account_identifier.trim(),
            smtp_host: form.smtp_host.trim(),
            smtp_port: Number(form.smtp_port),
            smtp_username: form.account_identifier.trim(),
            smtp_password: form.smtp_password,
            smtp_tls_ssl: form.smtp_tls_ssl,
        });

        if (!wasCreated) return;

        setForm(INITIAL_FORM);
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-3" noValidate>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                    <label
                        htmlFor="smtp-account-email"
                        className="text-xs font-semibold text-slate-700"
                    >
                        Account Email
                    </label>
                    <input
                        id="smtp-account-email"
                        type="email"
                        value={form.account_identifier}
                        onChange={(event) =>
                            handleFieldChange(
                                "account_identifier",
                                event.target.value,
                            )
                        }
                        placeholder="you@example.com"
                        aria-invalid={Boolean(errors.account_identifier)}
                        className={getFieldClassName(
                            Boolean(errors.account_identifier),
                        )}
                    />
                    <FieldError message={errors.account_identifier} />
                </div>

                <div>
                    <label
                        htmlFor="smtp-host"
                        className="text-xs font-semibold text-slate-700"
                    >
                        SMTP Host
                    </label>
                    <input
                        id="smtp-host"
                        type="text"
                        value={form.smtp_host}
                        onChange={(event) =>
                            handleFieldChange("smtp_host", event.target.value)
                        }
                        placeholder="smtp.example.com"
                        aria-invalid={Boolean(errors.smtp_host)}
                        className={getFieldClassName(
                            Boolean(errors.smtp_host),
                        )}
                    />
                    <FieldError message={errors.smtp_host} />
                </div>

                <div>
                    <label
                        htmlFor="smtp-port"
                        className="text-xs font-semibold text-slate-700"
                    >
                        Port
                    </label>
                    <input
                        id="smtp-port"
                        type="number"
                        inputMode="numeric"
                        min="1"
                        value={form.smtp_port}
                        onChange={(event) =>
                            handleFieldChange("smtp_port", event.target.value)
                        }
                        placeholder="587"
                        aria-invalid={Boolean(errors.smtp_port)}
                        className={getFieldClassName(
                            Boolean(errors.smtp_port),
                        )}
                    />
                    <FieldError message={errors.smtp_port} />
                </div>

                <div>
                    <label
                        htmlFor="smtp-password"
                        className="text-xs font-semibold text-slate-700"
                    >
                        Password
                    </label>
                    <PasswordInput
                        id="smtp-password"
                        value={form.smtp_password}
                        onChange={(event) =>
                            handleFieldChange(
                                "smtp_password",
                                event.target.value,
                            )
                        }
                        placeholder="Enter password"
                        hasError={Boolean(errors.smtp_password)}
                        inputClassName={getFieldClassName(
                            Boolean(errors.smtp_password),
                            "pl-3 pr-11",
                        )}
                    />
                    <FieldError message={errors.smtp_password} />
                </div>

                <div className="sm:col-span-2">
                    <label
                        htmlFor="smtp-tls-ssl"
                        className="text-xs font-semibold text-slate-700"
                    >
                        TLS/SSL
                    </label>
                    <div className="relative">
                        <select
                            id="smtp-tls-ssl"
                            value={form.smtp_tls_ssl}
                            onChange={(event) => {
                                handleFieldChange(
                                    "smtp_tls_ssl",
                                    event.target.value,
                                );
                                setIsTlsSelectOpen(false);
                            }}
                            onClick={() =>
                                setIsTlsSelectOpen((isOpen) => !isOpen)
                            }
                            onBlur={() => setIsTlsSelectOpen(false)}
                            onKeyDown={(event) => {
                                if (
                                    [
                                        "Enter",
                                        " ",
                                        "ArrowDown",
                                        "ArrowUp",
                                    ].includes(event.key)
                                ) {
                                    setIsTlsSelectOpen(true);
                                }
                                if (event.key === "Escape") {
                                    setIsTlsSelectOpen(false);
                                }
                            }}
                            aria-invalid={Boolean(errors.smtp_tls_ssl)}
                            className={`${getFieldClassName(
                                Boolean(errors.smtp_tls_ssl),
                            )} appearance-none pr-10 font-medium text-slate-800`}
                        >
                            {SMTP_TLS_OPTIONS.map((option) => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                        {isTlsSelectOpen ? (
                            <ChevronUp
                                size={15}
                                className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-slate-500"
                                aria-hidden="true"
                            />
                        ) : (
                            <ChevronDown
                                size={15}
                                className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-slate-500"
                                aria-hidden="true"
                            />
                        )}
                    </div>
                    <FieldError message={errors.smtp_tls_ssl} />
                </div>
            </div>

            <button
                type="submit"
                disabled={isSubmitting}
                className="inline-flex w-full items-center justify-center rounded-xl bg-gradient-to-r from-indigo-500 to-violet-500 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:from-indigo-600 hover:to-violet-600 focus:outline-none focus:ring-2 focus:ring-indigo-200 disabled:cursor-not-allowed disabled:from-indigo-300 disabled:to-violet-300"
            >
                {isSubmitting
                    ? "Adding..."
                    : "Add SMTP Connection"}
            </button>
        </form>
    );
};

export default SmtpConnectionForm;
